import { Center, Loader } from '@mantine/core';
import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { ForbiddenPage } from '@/features/errors/ForbiddenPage';

import { useAuth } from './auth-context';
import { usePermissions } from './permissions';

export const PASSWORD_ROUTE = '/securite/mot-de-passe';
export const MFA_ROUTE = '/securite/double-authentification';

export function FullPageLoader() {
  return (
    <Center h="100vh">
      <Loader />
    </Center>
  );
}

/**
 * Protège les pages connectées et impose la politique de sécurité :
 * changement de mot de passe puis activation de la MFA si le rôle l'exige (D-16).
 */
export function RequireAuth() {
  const { status, user } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <FullPageLoader />;
  if (status === 'anonymous' || !user) {
    return (
      <Navigate to="/connexion" replace state={{ from: location.pathname + location.search }} />
    );
  }
  if (user.must_change_password && location.pathname !== PASSWORD_ROUTE) {
    return <Navigate to={PASSWORD_ROUTE} replace />;
  }
  if (
    !user.must_change_password &&
    user.mfa_required &&
    !user.mfa_enabled &&
    location.pathname !== MFA_ROUTE
  ) {
    return <Navigate to={MFA_ROUTE} replace />;
  }
  return <Outlet />;
}

/** Affiche la page seulement si l'utilisateur a au moins une des permissions. */
export function RequirePermission({ anyOf, children }: { anyOf: string[]; children: ReactNode }) {
  const { canAny } = usePermissions();
  return canAny(anyOf) ? <>{children}</> : <ForbiddenPage />;
}

/** Rend `children` seulement si la permission est accordée. */
export function Can({
  perm,
  departmentId,
  children,
}: {
  perm: string;
  departmentId?: string | null;
  children: ReactNode;
}) {
  const { can } = usePermissions();
  return can(perm, { departmentId }) ? <>{children}</> : null;
}
